import { useAppContext } from 'Providers/AppProvider/AppContext';
import { useRfqId } from 'pages/auto-rfq/context/rfq-id';
import { publishCollabEvent } from 'lib/realtime/AblyClient';

/**
 * Client-side publisher for RFQ collaboration events. The backend isn't
 * emitting these on item writes, so the editing client broadcasts them on the
 * Spaces channel (the one browsers can publish to) and the other clients'
 * realtime hooks (useRfqLiveUpdates / useRfqInternalNotesRealtime) react. The
 * editor's own echo is filtered downstream by `actor.userId`.
 *
 * Item updates carry the editor's post-edit item in `after`, so receivers can
 * apply the change instantly without waiting on (or being bitten by stale)
 * refetches.
 */
export const useRfqRealtimeBroadcast = () => {
  // Resolve from RfqIdProvider when embedded (Quotes V2 Product Match tab routes
  // on `:quoteId`, so `useParams().id` is empty there); falls back to the route
  // param on the standalone Auto RFQ page (`/auto-rfq/:id`).
  const rfqId = useRfqId();
  const { user } = useAppContext();

  const actor = user?.ID
    ? {
        userId: String(user.ID),
        name:
          user.FullName ||
          `${user.FirstName || ''} ${user.LastName || ''}`.trim()
      }
    : undefined;

  const publish = (event: string, data: Record<string, unknown>) => {
    if (!rfqId) return;
    publishCollabEvent({ type: 'rfq', id: rfqId }, event, {
      rfqId,
      source: 'user',
      actor,
      ...data
    });
  };

  return {
    broadcastItemUpdate: (itemId?: string | null, after?: unknown) => {
      if (itemId) publish('rfq_item.updated', { itemId, after });
    },
    broadcastNoteAdded: (itemId?: string | null) => {
      if (itemId) publish('rfq_item.note_added', { itemId, note: {} });
    },
    broadcastNoteUpdated: (itemId?: string | null) => {
      if (itemId) publish('rfq_item.note_updated', { itemId, note: {} });
    },
    broadcastNoteDeleted: (itemId?: string | null, noteId?: string) => {
      if (itemId) publish('rfq_item.note_deleted', { itemId, noteId });
    },
    broadcastCounterChanged: (
      itemId: string | null | undefined,
      field: string,
      delta: number
    ) => {
      if (itemId) publish('rfq_item.counter_changed', { itemId, field, delta });
    }
  };
};
